import { Navigate, Route, Routes } from "react-router-dom";

import { useAuth, usePermissions } from "../hooks";
import { AdminLayout } from "../layouts";

import {
  AuditPage,
  CategoryPage,
  ClassificationPage,
  CreateCategoryPage,
  CreateClassificationPage,
  CreateProductPage,
  CreateRolePage,
  CreateSchemaPage,
  CreateUserPage,
  EditCategoryPage,
  EditClassificationPage,
  EditProductPage,
  EditRolePage,
  EditSchemaPage,
  EditUserPage,
  HomePage,
  InventoryAdjustmentPage,
  InventoryEntryPage,
  InventoryExitPage,
  InventoryMovementsPage,
  InventoryPage,
  ProductPage,
  RolesPage,
  SchemaPage,
  SettingsPage,
  UsersPage,
} from "../pages/Admin";

// Cada ruta del panel está atada al menú (PERMISSION_MENU_TREE, en
// utils/permissions.js) que la controla: sin VIS de ese menú, ni el
// listado ni sus páginas de crear/editar son accesibles, aunque se
// escriba la URL directamente. El orden también sirve para elegir a
// dónde mandar a alguien que cae en una ruta que no le corresponde.
const ADMIN_MENU_ROUTES = [
  { path: "/admin", menuKey: "dashboard" },
  { path: "/admin/products", menuKey: "products" },
  { path: "/admin/products/create", menuKey: "products" },
  { path: "/admin/products/:productId/edit", menuKey: "products" },
  { path: "/admin/categories", menuKey: "categories" },
  { path: "/admin/categories/create", menuKey: "categories" },
  { path: "/admin/categories/:categoryId/edit", menuKey: "categories" },
  { path: "/admin/inventory", menuKey: "inventory" },
  { path: "/admin/inventory/entry", menuKey: "inventory" },
  { path: "/admin/inventory/exit", menuKey: "inventory" },
  { path: "/admin/inventory/adjustment", menuKey: "inventory" },
  { path: "/admin/inventory/movements", menuKey: "inventory" },
  { path: "/admin/users", menuKey: "users" },
  { path: "/admin/users/create", menuKey: "users" },
  { path: "/admin/users/:userId/edit", menuKey: "users" },
  { path: "/admin/roles", menuKey: "roles" },
  { path: "/admin/roles/create", menuKey: "roles" },
  { path: "/admin/roles/:roleId/edit", menuKey: "roles" },
  { path: "/admin/settings", menuKey: "settings" },
  { path: "/admin/catalog/schema", menuKey: "catalog_schema" },
  { path: "/admin/catalog/schema/create", menuKey: "catalog_schema" },
  {
    path: "/admin/catalog/schema/:schemaId/edit",
    menuKey: "catalog_schema",
  },
  {
    path: "/admin/catalog/classification",
    menuKey: "catalog_classification",
  },
  {
    path: "/admin/catalog/classification/create",
    menuKey: "catalog_classification",
  },
  {
    path: "/admin/catalog/classification/:classificationId/edit",
    menuKey: "catalog_classification",
  },
  { path: "/admin/audit", menuKey: "audit" },
];

function getMenuKeyForPath(path) {
  const route = ADMIN_MENU_ROUTES.find((item) => item.path === path);

  return route ? route.menuKey : null;
}

function getFirstAccessiblePath(hasMenuAccess) {
  const route = ADMIN_MENU_ROUTES.find((item) =>
    hasMenuAccess(item.menuKey)
  );

  return route ? route.path : null;
}

export function AdminRouter() {
  const { isLoading } = useAuth();
  const { hasMenuAccess, hasAnyAccess } = usePermissions();

  if (isLoading) {
    return null;
  }

  if (!hasAnyAccess()) {
    return <Navigate to="/login" replace />;
  }

  function loadLayout(Page, path) {
    const menuKey = getMenuKeyForPath(path);

    if (menuKey && !hasMenuAccess(menuKey)) {
      const fallbackPath = getFirstAccessiblePath(hasMenuAccess);

      return <Navigate to={fallbackPath || "/login"} replace />;
    }

    return (
      <AdminLayout>
        <Page />
      </AdminLayout>
    );
  }

  return (
    <Routes>
      <Route path="/admin" element={loadLayout(HomePage, "/admin")} />

      <Route
        path="/admin/products"
        element={loadLayout(ProductPage, "/admin/products")}
      />

      <Route
        path="/admin/products/create"
        element={loadLayout(CreateProductPage, "/admin/products/create")}
      />

      <Route
        path="/admin/products/:productId/edit"
        element={loadLayout(
          EditProductPage,
          "/admin/products/:productId/edit"
        )}
      />

      <Route
        path="/admin/categories"
        element={loadLayout(CategoryPage, "/admin/categories")}
      />

      <Route
        path="/admin/categories/create"
        element={loadLayout(CreateCategoryPage, "/admin/categories/create")}
      />

      <Route
        path="/admin/categories/:categoryId/edit"
        element={loadLayout(
          EditCategoryPage,
          "/admin/categories/:categoryId/edit"
        )}
      />

      <Route
        path="/admin/inventory"
        element={loadLayout(InventoryPage, "/admin/inventory")}
      />

      <Route
        path="/admin/inventory/entry"
        element={loadLayout(InventoryEntryPage, "/admin/inventory/entry")}
      />

      <Route
        path="/admin/inventory/exit"
        element={loadLayout(InventoryExitPage, "/admin/inventory/exit")}
      />

      <Route
        path="/admin/inventory/adjustment"
        element={loadLayout(
          InventoryAdjustmentPage,
          "/admin/inventory/adjustment"
        )}
      />

      <Route
        path="/admin/inventory/movements"
        element={loadLayout(
          InventoryMovementsPage,
          "/admin/inventory/movements"
        )}
      />

      <Route
        path="/admin/users"
        element={loadLayout(UsersPage, "/admin/users")}
      />

      <Route
        path="/admin/users/create"
        element={loadLayout(CreateUserPage, "/admin/users/create")}
      />

      <Route
        path="/admin/users/:userId/edit"
        element={loadLayout(EditUserPage, "/admin/users/:userId/edit")}
      />

      <Route
        path="/admin/roles"
        element={loadLayout(RolesPage, "/admin/roles")}
      />

      <Route
        path="/admin/roles/create"
        element={loadLayout(CreateRolePage, "/admin/roles/create")}
      />

      <Route
        path="/admin/roles/:roleId/edit"
        element={loadLayout(EditRolePage, "/admin/roles/:roleId/edit")}
      />

      <Route
        path="/admin/settings"
        element={loadLayout(SettingsPage, "/admin/settings")}
      />

      <Route
        path="/admin/catalog/schema"
        element={loadLayout(SchemaPage, "/admin/catalog/schema")}
      />

      <Route
        path="/admin/catalog/schema/create"
        element={loadLayout(
          CreateSchemaPage,
          "/admin/catalog/schema/create"
        )}
      />

      <Route
        path="/admin/catalog/schema/:schemaId/edit"
        element={loadLayout(
          EditSchemaPage,
          "/admin/catalog/schema/:schemaId/edit"
        )}
      />

      <Route
        path="/admin/catalog/classification"
        element={loadLayout(
          ClassificationPage,
          "/admin/catalog/classification"
        )}
      />

      <Route
        path="/admin/catalog/classification/create"
        element={loadLayout(
          CreateClassificationPage,
          "/admin/catalog/classification/create"
        )}
      />

      <Route
        path="/admin/catalog/classification/:classificationId/edit"
        element={loadLayout(
          EditClassificationPage,
          "/admin/catalog/classification/:classificationId/edit"
        )}
      />

      <Route
        path="/admin/audit"
        element={loadLayout(AuditPage, "/admin/audit")}
      />

      <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}
